import {
    LOAD_HOUSING_PERIOD_PROPERTY,
    LOAD_HOUSING_PERIOD_PROPERTY_SUCCESS,
    LOAD_HOUSING_PERIOD_PROPERTY_ERROR,
    SET_SELECTED_FLOOR,
    SET_SELECTED_ROOM,
    SET_SELECTED_UNIT,
    SET_PROPERTY_FIELD,
    SET_FLOOR_FIELD,
    SET_UNIT_FIELD,
    SET_ROOM_FIELD,
    CLEAR_HOUSING_PERIOD_PROPERTY,
    SAVE_HOUSING_PERIOD_PROPERTY,
    SAVE_HOUSING_PERIOD_PROPERTY_SUCCESS,
    SAVE_HOUSING_PERIOD_PROPERTY_ERROR
} from "./housingPeriodPropertyActionTypes";
import { APARTMENT_COMPLEX } from "../../../pages/student-life-page/housing/housing-periods/housingPeriodConstants";
import {
    getApi,
    postApi
} from "../../../services/apiBase";
import { studentLifeAPIs } from "../../../services/apiURL";
import { showUpdateSuccessToast } from '../../../utils/Message/toasts';

// Define default floors and rooms which can be copied. These fields need initialized arrays for form controls to work properly.
const defaultFloor = { amenityIDs: [], programsIDs: [], glossarySportsIDs: [], yearClassificationIDs: [] };
const defaultRoom = { amenityIDs: [], programsIDs: [], glossarySportsIDs: [], yearClassificationIDs: [] };

export const loadHousingPeriodProperty = (substep, housingPeriodPropertyID) => async (dispatch) => {
    dispatch({ type: LOAD_HOUSING_PERIOD_PROPERTY, payload: housingPeriodPropertyID });

    try {
        const response = await getApi(`${studentLifeAPIs.housingPeriods.properties.getHousingPeriodProperty}/${housingPeriodPropertyID}`);

        // In addition to setting the loaded housing period property, also set the default floor, unit, and room for the property.
        const housingPeriodProperty = response.data.data;
        const { propertyTypeID, floors, units, rooms } = housingPeriodProperty;

        // Default the selected floor to the first element if one exists. If not, then set to the default
        const selectedFloor = floors.length > 0
            ? floors[0]
            : { ...defaultFloor };

        // Default unit will be the first unit on floor if property type is not dorm, empty object otherwise.
        const selectedFloorUnits = units.filter(u => u.housingPeriodFloorID === selectedFloor.housingPeriodFloorID);
        const selectedUnit = selectedFloorUnits.length > 0
            ? selectedFloorUnits[0]
            : {};

        // Default room will either be the first room for the unit, or the first room for the floor, depending on
        // property type. 
        let selectedRoom = { ...defaultRoom };
        if (propertyTypeID === APARTMENT_COMPLEX) {
            const selectedUnitRooms = rooms.filter(r => r.housingPeriodUnitID === selectedUnit.housingPeriodUnitID);
            selectedRoom = selectedUnitRooms.length > 0
                ? selectedUnitRooms[0]
                : { ...defaultRoom };
        }
        else {
            const selectedFloorRooms = rooms.filter(r => r.housingPeriodFloorID === selectedFloor.housingPeriodFloorID);
            selectedRoom = selectedFloorRooms.length > 0
                ? selectedFloorRooms[0]
                : { ...defaultRoom }
        };

        dispatch({
            type: LOAD_HOUSING_PERIOD_PROPERTY_SUCCESS, payload: {
                substep: substep,
                housingPeriodProperty: housingPeriodProperty,
                selectedFloor: selectedFloor,
                selectedUnit: selectedUnit,
                selectedRoom: selectedRoom
            }
        });
    }
    catch (err) {
        dispatch({ type: LOAD_HOUSING_PERIOD_PROPERTY_ERROR });
        console.error(err);
    }
}

export const setSelectedFloor = (housingPeriodProperty, page) => async (dispatch) => {

    // Unlike rooms and units which have specialized names and will be filtered on the frontend, the page for floors will
    // work as a one-based index into the property's floors.
    const { propertyTypeID, floors, units, rooms } = housingPeriodProperty;
    const selectedFloor = floors.length > 0
        ? { ...floors[page - 1] }
        : { ...defaultFloor };

    // Set the selected unit to the first unit belonging to that floor.
    const floorUnits = units.filter(u => u.housingPeriodFloorID === selectedFloor.housingPeriodFloorID);
    const selectedUnit = floorUnits.length > 0
        ? { ...floorUnits[0] }
        : {};

    // If the property is an apartment complex, the selected room is the first room on the first unit. Otherwise, set the
    // selected room to the first room of the floor.
    let selectedRoom = { ...defaultRoom };
    if (propertyTypeID === APARTMENT_COMPLEX) {
        const unitRooms = rooms.filter(r => r.housingPeriodUnitID === selectedUnit.housingPeriodUnitID);
        selectedRoom = unitRooms.length > 0
            ? unitRooms[0]
            : { ...defaultRoom };
    }
    else {
        const floorRooms = rooms.filter(r => r.housingPeriodFloorID === selectedFloor.housingPeriodFloorID);
        selectedRoom = floorRooms.length > 0
            ? floorRooms[0]
            : { ...defaultRoom };
    }
    
    dispatch({
        type: SET_SELECTED_FLOOR, payload:
        {
            selectedFloor: selectedFloor,
            selectedUnit: selectedUnit,
            selectedRoom: selectedRoom
        }
    });
}

export const setSelectedUnit = (housingPeriodProperty, selectedFloor, page) => async (dispatch) => {

    const floorUnits = housingPeriodProperty.units.filter(u => u.housingPeriodFloorID === selectedFloor.housingPeriodFloorID);
    const selectedUnit = { ...floorUnits[page - 1] };

    // Default the selected room to the first room of the unit
    const unitRooms = housingPeriodProperty.rooms.filter(r => r.housingPeriodUnitID === selectedUnit.housingPeriodUnitID);
    const selectedRoom = { ...unitRooms[0] };

    dispatch({
        type: SET_SELECTED_UNIT, payload: {
            selectedUnit: selectedUnit,
            selectedRoom: selectedRoom
        }
    });
}

export const setSelectedRoom = (housingPeriodProperty, selectedFloor, selectedUnit, page) => async (dispatch) => {

    // Selecting the room is awkward due to the room selector control. Because it is built on top of Material UI pagination, all the page
    // knows is which "page" was selected. Page is a one-based index on the rooms for the given floor or unit. To get from this to the full room details, 
    // we need to look up the rooms for the selected floor or unit, and then use the page as an index to select the selected room.
    const filteredRooms = housingPeriodProperty.propertyTypeID !== APARTMENT_COMPLEX
        ? housingPeriodProperty.rooms.filter(r => r.housingPeriodFloorID === selectedFloor.housingPeriodFloorID)
        : housingPeriodProperty.rooms.filter(r => r.housingPeriodUnitID === selectedUnit.housingPeriodUnitID);

    const selectedRoom = { ...filteredRooms[page - 1] };

    dispatch({ type: SET_SELECTED_ROOM, payload: selectedRoom });
}

export const setPropertyField = (housingPeriodProperty, selectedFloor, selectedRoom, fieldName, fieldValue) => async (dispatch) => {

    // For single-value dropdown fields, update all the floors and rooms to match the value on the property, as well as the 
    // selected floor and room
    const updatedSelectedFloor = { ...selectedFloor, [fieldName]: fieldValue };
    const updatedSelectedRoom = { ...selectedRoom, [fieldName]: fieldValue };

    const updatedHousingPeriodProperty = {
        ...housingPeriodProperty,
        [fieldName]: fieldValue,
        floors: housingPeriodProperty.floors.map(f => {
            return { ...f, [fieldName]: fieldValue }
        }),
        rooms: housingPeriodProperty.rooms.map(r => {
            return { ...r, [fieldName]: fieldValue }
        })
    };

    const payload = {
        updatedHousingPeriodProperty: updatedHousingPeriodProperty,
        updatedSelectedFloor: updatedSelectedFloor,
        updatedSelectedRoom: updatedSelectedRoom
    };

    dispatch({ type: SET_PROPERTY_FIELD, payload: payload })
}

export const setFloorField = (housingPeriodProperty, selectedFloor, selectedRoom, floorID, fieldName, fieldValue) => async (dispatch) => {

    // Create a copy of the property and update the floor with the new values.
    const updatedHousingPeriodProperty = { ...housingPeriodProperty };
    const updatedFloorIndex = housingPeriodProperty.floors.findIndex(f => f.housingPeriodFloorID === floorID);
    updatedHousingPeriodProperty.floors[updatedFloorIndex][fieldName] = fieldValue;

    // For single-value dropdown fields, update all the rooms for the given floor to match the value on the floor, as well as the
    // selected floor and room.
    const updatedSelectedFloor = { ...selectedFloor, [fieldName]: fieldValue };
    const updatedSelectedRoom = { ...selectedRoom, [fieldName]: fieldValue };
    updatedHousingPeriodProperty.rooms = updatedHousingPeriodProperty.rooms.map(r =>
    {
        if (r.housingPeriodFloorID === floorID) {
            return { ...r, [fieldName]: fieldValue }
        }
        else {
            return { ...r };
        }
    });

    const payload = {
        updatedHousingPeriodProperty: updatedHousingPeriodProperty,
        updatedSelectedFloor: updatedSelectedFloor,
        updatedSelectedRoom: updatedSelectedRoom
    };

    dispatch({ type: SET_FLOOR_FIELD, payload: payload })
}

export const setUnitField = (housingPeriodProperty, unitID, fieldName, fieldValue) => async (dispatch) => {

    // Create a copy of the property and update the unit with the new values.
    const updatedHousingPeriodProperty = { ...housingPeriodProperty };
    const updatedUnitIndex = housingPeriodProperty.units.findIndex(f => f.housingPeriodUnitID === unitID);
    updatedHousingPeriodProperty.units[updatedUnitIndex][fieldName] = fieldValue;

    dispatch({ type: SET_UNIT_FIELD, payload: updatedHousingPeriodProperty })
}

export const setRoomField = (housingPeriodProperty, roomID, fieldName, fieldValue) => async (dispatch) => {

    // Create a copy of the property and update the room with the new values.
    const updatedHousingPeriodProperty = { ...housingPeriodProperty };
    const updatedRoomIndex = housingPeriodProperty.rooms.findIndex(f => f.housingPeriodRoomID === roomID);
    updatedHousingPeriodProperty.rooms[updatedRoomIndex][fieldName] = fieldValue;

    dispatch({ type: SET_ROOM_FIELD, payload: updatedHousingPeriodProperty })
}

export const clearHousingPeriodProperty = () => async (dispatch) => {
    await dispatch({ type: CLEAR_HOUSING_PERIOD_PROPERTY });
}

export const saveHousingPeriodProperty = (housingPeriodProperty, selectedFloor, selectedUnit, selectedRoom) => async (dispatch) => {

    dispatch({ type: SAVE_HOUSING_PERIOD_PROPERTY });

    try {

        // Save the record and reload the saved property and selected floor/unit/room
        await postApi(studentLifeAPIs.housingPeriods.properties.save, housingPeriodProperty);
        
        const fetchResponse = await getApi(`${studentLifeAPIs.housingPeriods.properties.getHousingPeriodProperty}/${housingPeriodProperty.housingPeriodPropertyID}`);
        const updatedHousingPeriodProperty = fetchResponse.data.data;

        const newSelectedFloor = updatedHousingPeriodProperty.floors.filter(f => f.housingPeriodFloorID === selectedFloor.housingPeriodFloorID)[0];
        const newSelectedUnit = updatedHousingPeriodProperty.units.filter(f => f.housingPeriodUnitID === selectedUnit.housingPeriodUnitID)[0];
        const newSelectedRoom = updatedHousingPeriodProperty.rooms.filter(f => f.housingPeriodRoomID === selectedRoom.housingPeriodRoomID)[0];

        dispatch({
            type: SAVE_HOUSING_PERIOD_PROPERTY_SUCCESS, payload: {
                updatedHousingPeriodProperty: updatedHousingPeriodProperty,
                newSelectedFloor: newSelectedFloor,
                newSelectedUnit: newSelectedUnit,
                newSelectedRoom: newSelectedRoom
            }
        });
        
        showUpdateSuccessToast();
    }
    catch (err) {
        dispatch({ type: SAVE_HOUSING_PERIOD_PROPERTY_ERROR });
        console.error(err);
    }
}